import { UUID } from '@/common/utils';
import { METRICS_TYPES } from './metricsAggregation.defines';
import { MetricsAggregationFactory } from './MetricsAggregationFactory';

export type MetricsAggregationsType = (typeof METRICS_TYPES)[number];

export abstract class MetricsAggregation {
  id: UUID;

  type: MetricsAggregationsType;

  show = true;

  constructor(id: UUID, type: MetricsAggregationsType, show = true) {
    this.id = id;
    this.type = type;
    this.show = show;
  }

  abstract createClone(id: UUID): MetricsAggregation;

  abstract isValid(): boolean;

  abstract configure(config: unknown): void;

  abstract changeType(type: MetricsAggregationsType): MetricsAggregation;
}

class CountMetricsAggregationConfig {
  field: string;

  constructor(field: string = '') {
    this.field = field;
  }
}

export class CountMetricsAggregations extends MetricsAggregation {
  public config: CountMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<CountMetricsAggregationConfig>) {
    super(id, 'count', show);
    this.config = new CountMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new CountMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return true;
  }

  override configure(config: Partial<CountMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    }
    return newMetric;
  }
}

class BasicStatsMetricsAggregationConfig {
  field: string;

  script: string;

  missing: number | undefined;

  constructor(field: string = '', script: string = '', missing: number | undefined = undefined) {
    this.field = field;
    this.script = script;
    this.missing = missing;
  }
}

export class AvgMetricsAggregations extends MetricsAggregation {
  public config: BasicStatsMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<BasicStatsMetricsAggregationConfig>) {
    super(id, 'avg', show);
    this.config = new BasicStatsMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new AvgMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<BasicStatsMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    }
    return newMetric;
  }
}

export class MaxMetricsAggregations extends MetricsAggregation {
  public config: BasicStatsMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<BasicStatsMetricsAggregationConfig>) {
    super(id, 'max', show);
    this.config = new BasicStatsMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new MaxMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<BasicStatsMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ ...this.config });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    }
    return newMetric;
  }
}

export class MinMetricsAggregations extends MetricsAggregation {
  public config: BasicStatsMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<BasicStatsMetricsAggregationConfig>) {
    super(id, 'min', show);
    this.config = new BasicStatsMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new MinMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<BasicStatsMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ ...this.config });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    }
    return newMetric;
  }
}

export class SumMetricsAggregations extends MetricsAggregation {
  public config: BasicStatsMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<BasicStatsMetricsAggregationConfig>) {
    super(id, 'sum', show);
    this.config = new BasicStatsMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new SumMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<BasicStatsMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations
    ) {
      newMetric.configure({ ...this.config });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    }
    return newMetric;
  }
}

class PercentilesMetricsAggregationConfig {
  field: string;

  percentiles: string;

  script: string;

  missing: string;

  constructor(
    field: string = '',
    percentiles: string = '25, 50, 75, 95, 99',
    script: string = '',
    missing: string = '',
  ) {
    this.field = field;
    this.percentiles = percentiles;
    this.script = script;
    this.missing = missing;
  }
}

export class PercentilesMetricsAggregations extends MetricsAggregation {
  public config: PercentilesMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<PercentilesMetricsAggregationConfig>) {
    super(id, 'percentiles', show);
    this.config = new PercentilesMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new PercentilesMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<PercentilesMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({
        field: this.config.field,
        missing: this.config.missing,
      });
    }
    return newMetric;
  }
}

class RateMetricsAggregationConfig {
  field: string;

  unit: string;

  mode: string;

  script: string;

  constructor(
    field: string = '',
    unit: string = 'second',
    mode: string = 'sum',
    script: string = '',
  ) {
    this.field = field;
    this.unit = unit;
    this.mode = mode;
    this.script = script;
  }
}

export class RateMetricsAggregations extends MetricsAggregation {
  public config: RateMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<RateMetricsAggregationConfig>) {
    super(id, 'rate', show);
    this.config = new RateMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new RateMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<RateMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof StatsMetricsAggregations) {
      newMetric.configure({ field: this.config.field, script: this.config.script });
    }
    return newMetric;
  }
}

class StatsMetricsAggregationConfig {
  public field: string = '';

  public stats: string[] = ['avg', 'count', 'max', 'min', 'sum'];

  public script: string = '';

  public missing: string = '';

  constructor(
    field: string = '',
    stats: string[] = ['avg', 'count', 'max', 'min', 'sum'],
    script: string = '',
    missing: string = '',
  ) {
    this.field = field;
    this.stats = stats;
    this.script = script;
    this.missing = missing;
  }
}

export class StatsMetricsAggregations extends MetricsAggregation {
  public config: StatsMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<StatsMetricsAggregationConfig>) {
    super(id, 'stats', show);
    this.config = new StatsMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new StatsMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<StatsMetricsAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    if (newMetric instanceof CountMetricsAggregations) {
      newMetric.configure({ field: this.config.field });
    } else if (
      newMetric instanceof AvgMetricsAggregations ||
      newMetric instanceof MaxMetricsAggregations ||
      newMetric instanceof MinMetricsAggregations ||
      newMetric instanceof SumMetricsAggregations
    ) {
      newMetric.configure({ field: this.config.field });
    } else if (newMetric instanceof PercentilesMetricsAggregations) {
      newMetric.configure({ field: this.config.field, missing: this.config.missing });
    } else if (newMetric instanceof RateMetricsAggregations) {
      newMetric.configure({ field: this.config.field, script: this.config.script });
    }
    return newMetric;
  }
}

class TopMetricsMetricsAggregationConfig {
  metrics: string[];

  order: string;

  orderBy: string;

  constructor(metrics: string[] = [], order: string = 'asc', orderBy: string = '') {
    this.metrics = metrics;
    this.order = order;
    this.orderBy = orderBy;
  }
}

export class TopMetricsMetricsAggregations extends MetricsAggregation {
  public config: TopMetricsMetricsAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<TopMetricsMetricsAggregationConfig>) {
    super(id, 'topMetrics', show);
    this.config = new TopMetricsMetricsAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new TopMetricsMetricsAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.metrics.filter((metric) => metric.trim() !== '').length > 0;
  }

  override configure(config: Partial<TopMetricsMetricsAggregationConfig>) {
    Object.assign(this.config, config);
    if (this.config.order === 'top') {
      this.config.order = 'asc';
    } else if (this.config.order === 'bottom') {
      this.config.order = 'desc';
    }
  }

  override changeType(type: MetricsAggregationsType) {
    const newMetric = MetricsAggregationFactory.createMetricsAggregation(type);
    newMetric.id = this.id;
    return newMetric;
  }
}
